import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../../contexts/ThemeContext';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const darkSubmitReady = {
  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
  color: '#1d4746',
  boxShadow: '0 4px 14px rgba(251, 191, 36, 0.35)',
};
const darkSubmitDisabled = {
  background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
  color: '#64748b',
  boxShadow: 'none',
  opacity: 0.7,
};

const Pairing = ({ question, onAnswer, onNext, reviewMode, isCorrect, userAnswer, correctAnswer, sessionData = null, variant = 'default' }) => {
  const { isDarkMode } = useTheme();
  // Transform backend data to component format
  const transformedQuestion = useMemo(() => {
    const leftItems = [];
    const rightItems = [];
    const pairs = [];

    if (question.pairs && question.pairs.left && question.pairs.right) {
      // Transform left items
      if (Array.isArray(question.pairs.left)) {
        question.pairs.left.forEach((item) => {
          const idx = item.idx !== undefined ? item.idx : item.id;
          leftItems.push({
            id: idx,
            text: item['left-text'] || item.text || '',
          });
        });
      }

      // Transform right items
      if (Array.isArray(question.pairs.right)) {
        question.pairs.right.forEach((item) => {
          const idx = item.idx !== undefined ? item.idx : item.id;
          rightItems.push({
            id: idx,
            text: item['right-text'] || item.text || '',
          });
        });
      }

      // Build pairs: left and right items with same idx are paired
      const rightItemsMap = {};
      rightItems.forEach((item) => {
        rightItemsMap[item.id] = item;
      });

      leftItems.forEach((leftItem) => {
        const matchingRight = rightItemsMap[leftItem.id];
        if (matchingRight) {
          pairs.push({
            left: leftItem.id,
            right: matchingRight.id,
          });
        }
      });
    }

    return {
      ...question,
      leftItems: leftItems,
      rightItems: rightItems,
      pairs: pairs,
      text: question.text || question['text-soal'] || '',
    };
  }, [question]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [pairColors, setPairColors] = useState({});
  const [colorIndex, setColorIndex] = useState(0);
  const activeAnnotationRef = useRef(null);
  const annotationAudioRef = useRef(null);

  // Reset state when question changes
  useEffect(() => {
    if (reviewMode) {
      // In review mode, use provided answers
      if (userAnswer && Array.isArray(userAnswer)) {
        setPairs(userAnswer);
      }
    } else {
      setSelectedLeft(null);
      setSelectedRight(null);
      setPairs([]);
      setPairColors({});
      setColorIndex(0);
    }
    if (annotationAudioRef.current) {
      annotationAudioRef.current.pause();
      annotationAudioRef.current = null;
    }
  }, [question['soal-id'] || question.id, reviewMode, userAnswer]);

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    const html = transformedQuestion.text || '';
    if (html && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Pairing: failed to parse annotations from HTML', error);
        }
      }
    }

    return map;
  }, [question.annotations, transformedQuestion.text]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  const firstAnnotationAudioUrl = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
    try {
      const doc = new window.DOMParser().parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');
      for (const node of nodes) {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]?.streamUrl) {
          return annotationsMap[annId].streamUrl;
        }
      }
    } catch {}
    return null;
  }, [transformedQuestion.text, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl;

  // Enhance HTML with annotation classes; hide annotation shown via waveform player
  const enhancedHtml = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return html;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Pairing: failed to enhance HTML', error);
      }
      return html;
    }
  }, [transformedQuestion.text, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const handlePlayAnnotation = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;

    if (!annotationAudioRef.current) {
      annotationAudioRef.current = new Audio();
      annotationAudioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
    }
    const audioEl = annotationAudioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (event.target.closest('[data-pairing-item]')) return;

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  // Array of colors for pairs (no red colors to avoid looking like errors, all unique)
  const pairColorPalette = [
    '#4ECDC4', // Turquoise
    '#45B7D1', // Sky Blue
    '#F7DC6F', // Yellow
    '#BB8FCE', // Lavender
    '#F8B739', // Orange
    '#52BE80', // Green
    '#6C5CE7', // Purple
    '#00B894', // Mint Green
    '#00CEC9', // Cyan
    '#A29BFE'  // Light Purple
  ];

  const getPairColorKey = (leftId, rightId) => {
    return `${leftId}-${rightId}`;
  };

  const getPairColorForPair = (leftId, rightId) => {
    const key = getPairColorKey(leftId, rightId);
    return pairColors[key] ?? null;
  };

  const assignPairColor = (leftId, rightId) => {
    const key = getPairColorKey(leftId, rightId);
    const color = pairColorPalette[colorIndex % pairColorPalette.length];
    setPairColors(prev => ({ ...prev, [key]: color }));
    setColorIndex(prev => prev + 1);
    return color;
  };

  const handleLeftClick = (leftId) => {
    if (reviewMode) {
      return;
    }
    
    // If already paired, cancel pair
    const existingPair = pairs.find(pair => pair.left === leftId);
    if (existingPair) {
      const key = getPairColorKey(existingPair.left, existingPair.right);
      setPairs(pairs.filter(pair => pair.left !== leftId));
      setPairColors(prev => {
        const newColors = { ...prev };
        delete newColors[key];
        return newColors;
      });
      setSelectedLeft(null);
      setSelectedRight(null);
      return;
    }
    
    // If right is selected, create pair
    if (selectedRight !== null) {
      const newPair = { left: leftId, right: selectedRight };
      assignPairColor(leftId, selectedRight);
      setPairs([...pairs, newPair]);
      setSelectedLeft(null);
      setSelectedRight(null);
      return;
    }
    
    // Toggle selection
    if (selectedLeft === leftId) {
      setSelectedLeft(null);
    } else {
      setSelectedLeft(leftId);
    }
  };

  const handleRightClick = (rightId) => {
    if (reviewMode) {
      return;
    }
    
    // If already paired, cancel pair
    const existingPair = pairs.find(pair => pair.right === rightId);
    if (existingPair) {
      const key = getPairColorKey(existingPair.left, existingPair.right);
      setPairs(pairs.filter(pair => pair.right !== rightId));
      setPairColors(prev => {
        const newColors = { ...prev };
        delete newColors[key];
        return newColors;
      });
      setSelectedLeft(null);
      setSelectedRight(null);
      return;
    }
    
    // If left is selected, create pair
    if (selectedLeft !== null) {
      const newPair = { left: selectedLeft, right: rightId };
      assignPairColor(selectedLeft, rightId);
      setPairs([...pairs, newPair]);
      setSelectedLeft(null);
      setSelectedRight(null);
      return;
    }
    
    // Toggle selection
    if (selectedRight === rightId) {
      setSelectedRight(null);
    } else {
      setSelectedRight(rightId);
    }
  };

  const handleSubmit = () => {
    const correctPairs = transformedQuestion.pairs;
    const userPairs = pairs;
    
    const correct = userPairs.length === correctPairs.length &&
      correctPairs.every(correctPair => {
        return userPairs.some(userPair => 
          userPair.left === correctPair.left && userPair.right === correctPair.right
        );
      });
    
    if (onAnswer) {
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: userPairs,
        isCorrect: correct
      });
    }
    
    if (onNext) {
      onNext();
    }
  };

  const getPairForLeft = (leftId) => {
    return pairs.find(pair => pair.left === leftId);
  };

  const getPairForRight = (rightId) => {
    return pairs.find(pair => pair.right === rightId);
  };

  const getLeftColor = (leftId) => {
    const pair = getPairForLeft(leftId);
    if (pair) {
      return getPairColorForPair(pair.left, pair.right);
    }
    return null;
  };

  const getRightColor = (rightId) => {
    const pair = getPairForRight(rightId);
    if (pair) {
      return getPairColorForPair(pair.left, pair.right);
    }
    return null;
  };

  const getLeftClass = (leftId) => {
    if (selectedLeft === leftId) {
      return 'pairing-item-selected';
    }
    if (getPairForLeft(leftId)) {
      return 'pairing-item-paired';
    }
    return '';
  };

  const getRightClass = (rightId) => {
    if (selectedRight === rightId) {
      return 'pairing-item-selected';
    }
    if (getPairForRight(rightId)) {
      return 'pairing-item-paired';
    }
    return '';
  };

  // Render review mode: show correct answer and user answer per pair
  if (reviewMode) {
    const correctPairs = correctAnswer || transformedQuestion.pairs || [];
    const userPairs = userAnswer || pairs || [];
    
    // Build map for easy lookup
    const leftItemsMap = {};
    transformedQuestion.leftItems.forEach(item => {
      leftItemsMap[item.id] = item.text;
    });
    
    const rightItemsMap = {};
    transformedQuestion.rightItems.forEach(item => {
      rightItemsMap[item.id] = item.text;
    });
    
    // Get all left items that have pairs (from correct pairs)
    const allLeftIds = [...new Set(correctPairs.map(p => p.left))];
    
    // Get prosetId from question or sessionData as fallback
    const badgeGradient = variant === 'drill'
      ? 'from-[#345d8a] to-[#1e3d6f]'
      : 'from-[#12766d] to-[#236463]';

    return (
      <motion.div
        className="flex flex-col gap-5"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
          <motion.span
            className={`px-3.5 py-2 bg-gradient-to-br ${badgeGradient} text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1`}
            initial={{ scale: 0.85 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            Pairing
          </motion.span>
        </div>
        
        <div className="flex flex-col gap-5">
          {displayAudioUrl && (
            <QuestionAudioPlayer audioUrl={displayAudioUrl} />
          )}
          <p
            className="text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
            onClick={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationTrigger : undefined}
            onKeyDown={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationKeyDown : undefined}
            dangerouslySetInnerHTML={{ __html: enhancedHtml }}
          />

          <div className="flex flex-col gap-4 mt-6 max-md:gap-3 max-md:mt-5">
            {allLeftIds.map((leftId) => {
              const leftText = leftItemsMap[leftId] || '';
              const correctPair = correctPairs.find(p => p.left === leftId);
              const correctRightText = correctPair ? (rightItemsMap[correctPair.right] || '') : '';
              const userPair = userPairs.find(p => p.left === leftId);
              const userRightText = userPair ? (rightItemsMap[userPair.right] || '') : '';
              const isPairCorrect = correctPair && userPair && 
                correctPair.left === userPair.left && 
                correctPair.right === userPair.right;
              
              return (
                <div 
                  key={leftId} 
                  className={`relative p-4 rounded-xl border-2 bg-[var(--card-bg)] max-md:p-3.5 max-md:py-3 ${
                    isPairCorrect
                      ? 'border-[#10b981] bg-[rgba(16,185,129,0.1)]'
                      : 'border-[#ef4444] bg-[rgba(239,68,68,0.1)]'
                  }`}
                >
                  <div className="text-[15px] text-[var(--text-primary)] mb-2 leading-relaxed max-md:text-sm">
                    <strong className="font-semibold text-[var(--text-primary)]">Correct:</strong> {leftText} → {correctRightText}
                  </div>
                  {userPair && (
                    <div className="text-[15px] text-[#ef4444] leading-relaxed max-md:text-sm">
                      <strong className="font-semibold text-[#ef4444]">Your answer:</strong> {leftText} → {userRightText}
                    </div>
                  )}
                  {!isPairCorrect && (
                    <span className="absolute top-4 right-5 text-xl font-bold text-[#ef4444] max-md:top-3 max-md:right-4 max-md:text-lg">✗</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }

  const badgeGradient = variant === 'drill'
    ? 'from-[#345d8a] to-[#1e3d6f]'
    : 'from-[#12766d] to-[#236463]';

  const baseItemClass = 'pairing-item p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 bg-[var(--card-bg)] border-[rgba(18,118,109,0.2)] dark:border-[var(--border-color)] text-base text-[var(--text-primary)] relative max-md:p-3.5 max-md:text-[15px] max-md:min-h-[44px]';
  const hoverClass = 'hover:border-[#12766d] hover:bg-[rgba(18,118,109,0.05)] dark:hover:border-[#14b8a6] dark:hover:bg-[rgba(20,184,166,0.08)]';
  const selectedClass = 'pairing-item-selected !border-[#12766d] !bg-[rgba(18,118,109,0.15)] shadow-[0_0_0_3px_rgba(18,118,109,0.1)] dark:!border-[#14b8a6] dark:!bg-[rgba(20,184,166,0.15)]';
  // Jangan pakai pairedClass saat pairColor ada — inline style harus menang agar tiap pasang punya warna unik
  const pairedClass = 'pairing-item-paired !border-[#10b981] !bg-[#d1fae5] dark:!bg-[rgba(16,185,129,0.1)]';

  const getItemClass = (pairColor, itemClass) => {
    if (pairColor) return ''; // Inline style handles border/background per pair
    if (itemClass === 'pairing-item-paired') return pairedClass;
    if (itemClass === 'pairing-item-selected') return selectedClass;
    return hoverClass;
  };

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.span
          className={`px-3.5 py-2 bg-gradient-to-br ${badgeGradient} text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1`}
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Pairing
        </motion.span>
      </div>
      
      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer audioUrl={displayAudioUrl} />
        )}
        <p
          className="text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
          onClick={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationTrigger : undefined}
          onKeyDown={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationKeyDown : undefined}
          dangerouslySetInnerHTML={{ __html: enhancedHtml }}
        />

        <div className="grid grid-cols-2 gap-6 mb-6 max-md:gap-3 max-md:mb-5">
          <div className="flex flex-col gap-3">
            {transformedQuestion.leftItems && transformedQuestion.leftItems.length > 0 && (
              <h4 className="text-base font-semibold text-[var(--text-primary)] mb-2 max-md:text-[15px]">
                {transformedQuestion.leftColumnTitle || 'Left Column'}
              </h4>
            )}
            {transformedQuestion.leftItems.map((item) => {
              const pairColor = getLeftColor(item.id);
              const leftClass = getLeftClass(item.id);
              return (
                <div
                  key={item.id}
                  data-pairing-item
                  className={`${baseItemClass} ${getItemClass(pairColor, leftClass)}`}
                  onClick={() => handleLeftClick(item.id)}
                  style={pairColor ? { 
                    backgroundColor: `${pairColor}20`,
                    borderColor: pairColor,
                    borderWidth: '2px',
                    borderStyle: 'solid'
                  } : {}}
                >
                  {item.text}
                </div>
              );
            })}
          </div>
          
          <div className="flex flex-col gap-3">
            {transformedQuestion.rightItems && transformedQuestion.rightItems.length > 0 && (
              <h4 className="text-base font-semibold text-[var(--text-primary)] mb-2 max-md:text-[15px]">
                {transformedQuestion.rightColumnTitle || 'Right Column'}
              </h4>
            )}
            {transformedQuestion.rightItems.map((item) => {
              const pairColor = getRightColor(item.id);
              const rightClass = getRightClass(item.id);
              return (
                <div
                  key={item.id}
                  data-pairing-item
                  className={`${baseItemClass} ${getItemClass(pairColor, rightClass)}`}
                  onClick={() => handleRightClick(item.id)}
                  style={pairColor ? { 
                    backgroundColor: `${pairColor}20`,
                    borderColor: pairColor,
                    borderWidth: '2px',
                    borderStyle: 'solid'
                  } : {}}
                >
                  {item.text}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:pt-3 max-md:pb-20">
        <motion.button
          className="px-8 py-3 rounded-xl text-base font-semibold cursor-pointer transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-br from-[#fcb319] to-[#f5a500] text-[var(--text-primary)] shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:bg-gradient-to-br hover:from-[#f5a500] hover:to-[#e69500] hover:shadow-[0_4px_8px_rgba(252,179,25,0.3)] hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-[0_2px_4px_rgba(252,179,25,0.2)] max-md:w-full max-md:py-3.5 max-md:min-h-[44px]"
          onClick={handleSubmit}
          disabled={pairs.length < transformedQuestion.leftItems.length}
          style={isDarkMode ? (pairs.length < transformedQuestion.leftItems.length ? darkSubmitDisabled : darkSubmitReady) : {}}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Pairing;

